<!--
  ~ Copyright (c) GM Global Technology Operations LLC. All rights reserved.
  ~ This information is confidential and proprietary to GM Global Technology
  ~ Operations LLC and may not be used, modified, copied or distributed.
  -->

<fab-loading-spinner color="primary" *ngIf="showSpinner"
                     id="loading-spinner"></fab-loading-spinner>
<div id="header-container">
  <div>
    <h1 id="header-text">Vehicle Volume Analysis Report</h1>
  </div>
  <div>
    <button mat-flat-button color="primary" id="report-button" class="action-button"
            [disabled]="!(selectedReportType && selectedSegmentType && areScenariosSelected)"
            (click)="onRunReport()">
      Run Report
    </button>
  </div>
</div>
<div id="main-content" class="d-flex gap-16 align-content-between">
  <div id="filters" class="filter-container d-flex flex-column gap-8">

    <mat-form-field class="default-margin report-type" subscriptSizing="dynamic">
      <mat-label class="asterisk--before">Report Type</mat-label>
      <mat-select id="report-type-select" [(ngModel)]="selectedReportType" (selectionChange)="setScenariosSelected()">
        <mat-option [value]="'WorkingVsCompare'">Working Scenario vs Compare Scenario</mat-option>
        <mat-option [value]="'YrOverYr'">Year Over Year in Working Scenario</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="default-margin" subscriptSizing="dynamic">
      <mat-label class="asterisk--before">Segment Type</mat-label>
      <mat-select id="segment-type-select" [(ngModel)]="selectedSegmentType">
        <mat-option [value]="'GSEG'">Global Segment</mat-option>
        <mat-option [value]="'PROP_SEG'">Propulsion Segment</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="default-margin" subscriptSizing="dynamic">
      <mat-label class="asterisk--before">Start Year</mat-label>
      <mat-select [(ngModel)]="startYear" id="startYear">
        <mat-option *ngFor="let year of yearRange" [value]="year">{{ year }}</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field class="default-margin" subscriptSizing="dynamic">
      <mat-label class="asterisk--before">End Year</mat-label>
      <mat-select [(ngModel)]="endYear" id="endYear">
        <mat-option *ngFor="let year of yearRange" [value]="year">{{ year }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-checkbox [(ngModel)]="aggregateTos"
                  matTooltip="If checked, it will aggregate Type of Sale to TOSND in the industry scenario data sheets">
      Aggregate Type of Sale
    </mat-checkbox>
  </div>
  <div id="scenario-content" class="d-flex flex-column gap-8 w-100">
    <!-- Scenario type toggle -->
    <div class="d-flex flex-column justify-content-center">
      <mat-button-toggle-group [(ngModel)]="isGlobal"
                               hideSingleSelectionIndicator="true"
                               (ngModelChange)="resetGlobalScenarios()">
        <mat-button-toggle id="forecast-org-toggle" [value]="false" class="flex-grow-1">
          By Forecast Org
        </mat-button-toggle>
        <mat-button-toggle id="global-scenario-toggle" [value]="true" class="flex-grow-1">
          By Global Scenario
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>
    <!--If user is working with Vehicle Scenarios -->
    <div id="scenario-container" *ngIf="!isGlobal">
      <table [ngClass]="{'half-width': selectedReportType === 'YrOverYr'}">
        <tr>
          <td></td>
          <td class="working-scenario-content-border working-scenario-content-border-top column-header">
            <h2><b>Working Scenario</b></h2>
            <h3 *ngIf="!selectedWorkingScenario" style="font-style: italic">Select Scenario
              Below</h3>
          </td>
          <td class="column-header" *ngIf="selectedReportType === 'WorkingVsCompare'">
            <h2 [ngClass]="{'disabled-text': !selectedWorkingScenario}"><b>Compare Scenario</b></h2>
          </td>
        </tr>

        <!-- FORECAST ORG ROW -->
        <tr>
          <td class="scenario-labels">Forecast Organization</td>
          <td *ngIf="selectedWorkingScenario" class="working-scenario-content-border">
            <div class="column-content">
              <span class="column-text">{{ selectedWorkingScenario.fcorg }}</span>
            </div>
          </td>
          <td *ngIf="selectedCompareScenario && selectedReportType === 'WorkingVsCompare'">
            <div class="column-content">
              <span class="column-text">{{ selectedCompareScenario.fcorg }}</span>
            </div>
          </td>
          <!-- if no selected scenarios -->
          <td *ngIf="!selectedWorkingScenario" class="working-scenario-content-border column-content"></td>
          <td *ngIf="!selectedCompareScenario && selectedReportType === 'WorkingVsCompare'"></td>
        </tr>

        <!-- VEHICLE SCENARIO ROW -->
        <tr class="light-gray-background">
          <td class="scenario-labels">Vehicle Scenario</td>
          <td *ngIf="selectedWorkingScenario"
              class="working-scenario-content-border">
            <div class="column-content">
            <span class="column-text pad-left-50">{{ selectedWorkingScenario.vehScenId }}
              - {{ selectedWorkingScenario.vehScenName }}</span>
              <button mat-icon-button [matMenuTriggerFor]="vehicleMenu" class="vertical-dots" matTooltip="More options">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #vehicleMenu="matMenu">
                <button mat-menu-item (click)="openVehSelectComponent()" class="menu-width">
                  <mat-icon>autorenew</mat-icon>
                  <span>Change Scenario</span>
                </button>
              </mat-menu>
            </div>
          </td>
          <td *ngIf="selectedCompareScenario && selectedReportType === 'WorkingVsCompare'">
            <div class="column-content">
            <span class="column-text pad-left-50">{{ selectedCompareScenario.vehScenId }}
              - {{ selectedCompareScenario.vehScenName }}</span>
              <button mat-icon-button [matMenuTriggerFor]="compareVehicleMenu" class="vertical-dots"
                      matTooltip="More options">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #compareVehicleMenu="matMenu">
                <button mat-menu-item (click)="selectCompareScenario()" class="menu-width">
                  <mat-icon>autorenew</mat-icon>
                  <span>Change Scenario</span>
                </button>
              </mat-menu>
            </div>
          </td>
          <!-- if no selected scenarios -->
          <td *ngIf="!selectedWorkingScenario" class="working-scenario-content-border column-content">
          <span class="hyperlink"
                tabindex="0"
                (click)="openVehSelectComponent()"
                (keydown.enter)="openVehSelectComponent()">Select Vehicle Scenario</span>
          </td>
          <td *ngIf="!selectedCompareScenario && selectedReportType === 'WorkingVsCompare'"></td>
        </tr>

        <!-- INDUSTRY SCENARIO ROW -->
        <tr>
          <td class="scenario-labels">Industry Scenario</td>
          <td *ngIf="selectedWorkingScenario"
              class="working-scenario-content-border working-scenario-content-border-bottom">
            <div class="column-content">
            <span class="column-text">{{ selectedWorkingScenario.industryScenario.id }}
              - {{ selectedWorkingScenario.industryScenario.industryScenarioName }}</span>
            </div>
          </td>
          <td *ngIf="selectedCompareScenario && selectedReportType === 'WorkingVsCompare'">
            <div class="column-content">
            <span class="column-text">{{ selectedCompareScenario.industryScenario.id }}
              - {{ selectedCompareScenario.industryScenario.industryScenarioName }}</span>
            </div>
          </td>
          <!-- if no selected scenarios -->
          <td *ngIf="!selectedWorkingScenario"
              class="working-scenario-content-border working-scenario-content-border-bottom column-content"></td>
          <td *ngIf="!selectedCompareScenario && selectedReportType === 'WorkingVsCompare'"></td>
        </tr>

      </table>
    </div>
    <div *ngIf="isGlobal">
      <ng-container>
        <h3>Working Global Scenario</h3>
        <app-global-scenario-select-component
          class="content-height d-flex"
          [user]="stateService.getUserGmid()"
          [filterNonGM]="true"
          [memberScenarioType]="0"
          (confirmEvent)="onGlobalScenarioSelect($event)"></app-global-scenario-select-component>
      </ng-container>

      <mat-divider></mat-divider>
      <!-- Only show the compare scenario selection if they are using the Working vs Compare report type -->
      <ng-container *ngIf="selectedReportType === 'WorkingVsCompare'">
        <h3>Compare Global Scenario</h3>
        <app-global-scenario-select-component
          class="content-height d-flex"
          [user]="stateService.getUserGmid()"
          [filterNonGM]="true"
          [memberScenarioType]="0"
          (confirmEvent)="onCompareGlobalScenarioSelect($event)"></app-global-scenario-select-component>
      </ng-container>
    </div>
  </div>
</div>

